import { useNavigate } from "@tanstack/react-router";
import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "#/components/ui/button";
import { Input } from "#/components/ui/input";

type SearchInputProps = {
	value: string;
};

export function SearchInput({ value: urlValue }: SearchInputProps) {
	const { t } = useTranslation("browse");
	const [value, setValue] = useState(urlValue);
	const navigate = useNavigate({ from: "/listings/" });
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Keep local state in sync when URL param changes externally
	// (e.g. ActivePills removes the "Search: …" pill)
	useEffect(() => {
		setValue(urlValue);
	}, [urlValue]);

	function pushQ(q: string) {
		navigate({
			search: (prev) => ({ ...prev, q }),
			replace: true,
			viewTransition: false,
		});
	}

	function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
		const next = e.target.value;
		setValue(next);

		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => {
			pushQ(next);
		}, 300);
	}

	function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
		if (e.key === "Enter") {
			if (debounceRef.current) clearTimeout(debounceRef.current);
			pushQ(value);
		}
	}

	function handleClear() {
		if (debounceRef.current) clearTimeout(debounceRef.current);
		setValue("");
		pushQ("");
	}

	return (
		<div className="relative flex items-center">
			<Input
				type="search"
				placeholder={t("searchPlaceholder")}
				value={value}
				onChange={handleChange}
				onKeyDown={handleKeyDown}
				className="h-9 w-full pr-8 sm:w-[220px]"
				aria-label={t("searchListings")}
			/>
			{value && (
				<Button
					type="button"
					variant="ghost"
					size="icon-xs"
					className="absolute right-1.5 text-muted-foreground hover:text-foreground"
					onClick={handleClear}
					aria-label={t("clearSearch")}
				>
					<X />
				</Button>
			)}
		</div>
	);
}
